import { containsPoint, drawsAt, inflate, ROOT_ID, type Rect, type Scene } from '@flobi/artwork-core';
import type { Point } from './tools/types';

/**
 * Where on the canvas a click is picking: the groups the selection sits inside.
 *
 * A click takes a whole group, because a group is one thing on the page. Once something
 * inside a group is chosen, though — by a double click, a ⌘-click, the layers list — the
 * next click picks among its neighbours, however deep they are nested, and a click outside
 * that group steps back out one level at a time. The scope is therefore read from the
 * selection rather than kept on the side: whatever chose a layer put you where it lives.
 */
export class GroupEditing {
  private stack: string[] = [];
  constructor(private scene: Scene, private bounds: (id: string) => Rect | null) {}

  get current(): string | null {
    while (this.stack.length && (this.scene.get(this.stack.at(-1)!)?.type !== 'group' || !this.scene.choosable(this.stack.at(-1)!))) this.stack.pop();
    return this.stack.at(-1) ?? null;
  }

  enter(id: string): boolean {
    if (this.scene.get(id)?.type !== 'group' || !this.scene.choosable(id)) return false;
    this.stack = this.chain(id);
    return true;
  }

  exit(): string | null {
    const id = this.current;
    this.stack.pop();
    return id;
  }

  clear(): void { this.stack = []; }

  /**
   * The scope follows what was chosen: the groups round it, when it all sits in one group,
   * and none otherwise. Choosing nothing keeps the place — a click between a group's
   * children has not left the group.
   */
  acceptSelection(ids: readonly string[]): void {
    if (ids.length === 0) return;
    const parents = new Set(ids.map(id => this.scene.parentOf(id)));
    const parent = parents.size === 1 ? [...parents][0] : undefined;
    if (parent && this.scene.get(parent)?.type === 'group' && this.scene.choosable(parent)) this.stack = this.chain(parent);
    else this.clear();
  }

  /** Steps out of every group the point is outside of, innermost first. */
  retreat(point: Point, tolerance: number): void {
    while (this.current && !this.contains(point, tolerance)) this.stack.pop();
  }

  contains(point: Point, tolerance: number): boolean {
    const current = this.current;
    const box = current ? this.bounds(current) : null;
    return !!box && containsPoint(inflate(box, tolerance), point.x, point.y);
  }

  /**
   * The layer a click takes inside the scope: the innermost group's children first, then
   * each group round it, so a neighbour one level up is still one click away. Null when
   * nothing in the scope draws there, and the page's own rules take over.
   */
  hit(point: Point, tolerance: number): string | null {
    if (!this.current) return null;
    for (let level = this.stack.length - 1; level >= 0; level--) {
      const children = this.scene.childrenOf(this.stack[level]!);
      for (let i = children.length - 1; i >= 0; i--) {
        const id = children[i]!;
        const node = this.scene.get(id);
        const box = this.bounds(id);
        if (!node || node.type === 'effector' || node.type === 'slice' || !this.scene.choosable(id) || !box) continue;
        if (containsPoint(inflate(box, tolerance), point.x, point.y) && drawsAt(this.scene, id, point, tolerance)) return id;
      }
    }
    return null;
  }

  /** The groups from the outermost down to `id`, stopping at anything that is not one. */
  private chain(id: string): string[] {
    const out: string[] = [];
    let at: string | undefined = id;
    while (at && at !== ROOT_ID && this.scene.get(at)?.type === 'group') {
      out.unshift(at);
      at = this.scene.parentOf(at);
    }
    return out;
  }
}
